import type { ChangeAction, Deployment, PlanChange, RunSummary, SharedChange } from '../api/types';

export type PlanCounts = Record<ChangeAction, number>;

export function countActions(changes: readonly { action: ChangeAction }[]): PlanCounts {
  const counts: PlanCounts = { create: 0, update: 0, replace: 0, delete: 0, 'no-change': 0 };
  for (const change of changes) counts[change.action]++;
  return counts;
}

/** "2 to create, 1 to update, 0 to delete"; replacements are listed when there are any. */
export function planSummary(changes: readonly PlanChange[]): string {
  const counts = countActions(changes);
  const parts = [`${counts.create} to create`, `${counts.update} to update`];
  if (counts.replace > 0) parts.push(`${counts.replace} to replace`);
  parts.push(`${counts.delete} to delete`);
  return parts.join(', ');
}

export function hasChanges(changes: readonly PlanChange[], shared: readonly SharedChange[] = []): boolean {
  return [...changes, ...shared].some((change) => change.action !== 'no-change');
}

/** Display order: the most destructive first, unchanged last. */
const ORDER: Record<ChangeAction, number> = { delete: 0, replace: 1, update: 2, create: 3, 'no-change': 4 };

export function sortChanges<T extends { action: ChangeAction }>(changes: readonly T[]): T[] {
  return [...changes].sort((a, b) => ORDER[a.action] - ORDER[b.action]);
}

/**
 * The reviewed plan to offer for an apply (spec 6.4): the latest completed plan, when it was made for the latest
 * version and the deployment's current settings (equal `settingsHash`; a null hash never equals).
 */
export function currentPlan(
  plan: RunSummary | null | undefined,
  deployment: Pick<Deployment, 'settingsHash'>,
  latestVersion: number,
): RunSummary | null {
  if (!plan || plan.settingsHash === null || deployment.settingsHash === null) return null;
  return plan.settingsHash === deployment.settingsHash && plan.blueprintVersion === latestVersion
    ? plan
    : null;
}
